import React, {useState} from 'react';
import useSolvedStatus from '@utils/hooks/useSolvedStatus';
import {isMobile} from 'react-device-detect';
import {Modal} from '@goproblems/ui';
import {Button} from '@goproblems/ui';
import i18next from 'i18next';
import {DailyProblemV2} from '@types';

type ProblemsComponentProps = {
    dailyProblems: DailyProblemV2[];
    compactLayout?: boolean;
};

type FullButtonsProps = {
    dailyProblems: DailyProblemV2[];
    solvedStatus: Record<string, boolean>;
};

type MobileButtonsProps = FullButtonsProps & {
    levelName: string;
};

const t = i18next.t;

const getBtnColorClass = (solved: boolean, notSolvedAttempts: boolean) => {
    if (solved) {
        return notSolvedAttempts ? 'btn-success-subtle' : 'btn-success';
    } else {
        return notSolvedAttempts ? 'btn-danger-subtle' : 'btn-secondary';
    }
};

const FullButtons: React.FC<FullButtonsProps> = ({
    dailyProblems,
    solvedStatus,
}) => {
    // In UX React component use global i18next.t instead
    // const {t} = useTranslation();
    return dailyProblems.map(entry => {
        const level = entry.category;
        const solvedByCurrentUser =
            solvedStatus[level] ?? entry.solvedByCurrentUser;
        const notSolvedAttempts = entry.firstSolveAttemptFailedByCurrentUser;

        return (
            <Button
                key={entry.id}
                id={`potd-btn-${level}`}
                className={`btn mx-1 !rounded ${getBtnColorClass(solvedByCurrentUser, notSolvedAttempts ?? false)}`}
                variant={
                    solvedByCurrentUser
                        ? notSolvedAttempts
                            ? 'success-subtle'
                            : 'success'
                        : 'secondary'
                }
                href={`/problems/${entry.id}?name=${level}&dailyProblem=1`}
                data-not-solved-attempts={notSolvedAttempts ? '1' : '0'}>
                {solvedByCurrentUser && (
                    <i className="m-1 fa-solid fa-circle-check"></i>
                )}
                {t(`index.potd.${level}`)}
            </Button>
        );
    });
};

const MobileButtons: React.FC<MobileButtonsProps> = ({
    dailyProblems,
    solvedStatus,
    levelName,
}) => {
    return dailyProblems.map(entry => {
        const level = entry.category;
        const solvedByCurrentUser =
            solvedStatus[level] ?? entry.solvedByCurrentUser;
        const notSolvedAttempts = entry.firstSolveAttemptFailedByCurrentUser;
        const isCurrentLevel = level === levelName;

        return (
            <span
                key={entry.id}
                id={`potd-mobile-btn-${level}`}
                className={`mobile-potd-btn ${getBtnColorClass(solvedByCurrentUser, notSolvedAttempts ?? false)} ${isCurrentLevel ? 'current' : ''}`}
                data-not-solved-attempts={notSolvedAttempts ? '1' : '0'}>
                {level.charAt(0).toUpperCase()}
            </span>
        );
    });
};

const DailyProblemsButtons: React.FC<ProblemsComponentProps> = ({
    dailyProblems,
    compactLayout = isMobile,
}) => {
    const [showModal, setShowModalModal] = useState<boolean>(false);
    const {solvedStatus, levelName} = useSolvedStatus();

    const handleClose = () => setShowModalModal(false);
    const handleShow = () => setShowModalModal(true);

    if (dailyProblems.length === 0) {
        return <></>;
    }

    return (
        <span className="potd-difficulty-buttons shrink-0">
            {compactLayout ? (
                <span
                    className={'mobile-button-container'}
                    onClick={handleShow}>
                    <MobileButtons
                        dailyProblems={dailyProblems}
                        solvedStatus={solvedStatus}
                        levelName={levelName}
                    />
                </span>
            ) : (
                <FullButtons
                    dailyProblems={dailyProblems}
                    solvedStatus={solvedStatus}
                />
            )}
            {compactLayout && (
                <Modal show={showModal} onHide={handleClose}>
                    <Modal.Header closeButton>
                        <Modal.Title>{t('index.potd.go_to')}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <span
                            className={
                                'd-flex cursor-pointer justify-content-center align-items-center gap-1 font-size-2-rem'
                            }>
                            <FullButtons
                                dailyProblems={dailyProblems}
                                solvedStatus={solvedStatus}
                            />
                        </span>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleClose}>
                            {t('common.close')}
                        </Button>
                    </Modal.Footer>
                </Modal>
            )}
        </span>
    );
};

export default DailyProblemsButtons;
